import * as React from 'react';

// =============================================================================
// use-scroll-to-end.ts — Détection « scrollé jusqu'au bout » d'une zone (générique).
// =============================================================================
// Primitive RENDERER-AGNOSTIQUE : observe la position de défilement d'un élément
// scrollable et signale quand l'INTÉGRALITÉ a été parcourue. Elle ne sait RIEN du
// contenu (placeholder S5, pdf.js S7, …) — elle ne lit que `scrollTop/scrollHeight/
// clientHeight` de l'élément qu'on lui confie. C'est ce découplage qui permet, en
// S7, de remplacer l'implémentation interne du viewer par pdf.js derrière la MÊME
// abstraction (DocumentViewer), sans re-toucher le flux d'acceptation.
//
// Sémantique :
//   - si le contenu tient entièrement dans la zone (pas de débordement), il est
//     considéré parcouru — mais SEULEMENT une fois le contenu peuplé et ses dimensions
//     stabilisées (jamais sur un conteneur encore vide, cf. section ci-dessous) ;
//   - sinon, il est considéré parcouru quand le défilement atteint le bas (avec une
//     marge de tolérance pour les arrondis sub-pixel).
//
// Le hook renvoie un CALLBACK REF à poser sur la zone scrollable et un booléen
// `reachedEnd`. Le callback ref garantit que le listener est attaché DÈS QUE
// l'élément apparaît dans le DOM (un ref objet + effet manquerait un montage tardif).
//
// ── POPULATION TARDIVE DU CONTENU (impact LÉGAL) ────────────────────────────────
// La zone scrollable est souvent VIDE au moment où le callback ref s'attache : le
// contenu est injecté APRÈS (RichHtmlView pose `innerHTML` dans un useEffect passif ;
// pdf.js ajoute ses pages de façon asynchrone). Mesurer `scrollHeight/clientHeight` à
// cet instant donnerait `fitsEntirely=true` sur un conteneur vide → la gate de lecture
// forcée (publication Admin ST-CGU-03) et la signature CGU fournisseur (S31) seraient
// contournables DÈS l'ouverture. Deux garde-fous :
//   1. on n'établit JAMAIS `fitsEntirely` sur la mesure initiale synchrone ni sur un
//      simple placeholder : la conclusion « tient entièrement » est RÉSERVÉE à un
//      contenu STABILISÉ après population (observé via MutationObserver + debounce de
//      stabilisation), et une hauteur NON NULLE ;
//   2. `reachedEnd` n'est PAS un latch monotone : si le contenu déborde à nouveau (ou
//      est réinjecté) et que le bas n'est pas atteint, il repasse à `false`.
//
// Aucune persistance : état React in-memory (RULE_REF_006).

const SCROLL_TOLERANCE_PX = 4;

// Fenêtre de stabilisation : après la DERNIÈRE mutation du contenu (injection HTML,
// ajout/rendu des pages pdf.js, y compris le `replaceChildren()` transitoire qui vide
// le conteneur), on attend que les dimensions se figent avant de conclure « tient
// entièrement ». Coalesce les rafales de mutations ; imperceptible à l'usage.
const CONTENT_SETTLE_MS = 120;

export interface ScrollToEnd {
  /** À poser sur l'élément scrollable (callback ref). */
  readonly ref: (el: HTMLElement | null) => void;
  /** Vrai dès que la zone a été parcourue jusqu'au bas (ou tient en entier). */
  readonly reachedEnd: boolean;
  /**
   * Ratio de progression de lecture dans [0, 1] : part du contenu déjà parcourue.
   * 0 avant toute mesure exploitable ; 1 quand le contenu tient entièrement (rien à
   * faire défiler) ou quand le bas est atteint. Extension ADDITIVE (les consommateurs
   * existants ne destructurent que `ref`/`reachedEnd`) — sert à alimenter une barre de
   * progression a11y (role="progressbar") sans coupler la détection au renderer.
   */
  readonly progress: number;
}

export function useScrollToEnd(): ScrollToEnd {
  const [reachedEnd, setReachedEnd] = React.useState(false);
  const [progress, setProgress] = React.useState(0);
  // Conserve le détacheur du listener courant (changement d'élément / démontage).
  const cleanupRef = React.useRef<(() => void) | null>(null);

  // Mesure la zone. `concludeFits` autorise (ou non) à conclure « tient entièrement »
  // (donc parcouru sans défilement) : réservé aux mesures POST-STABILISATION du contenu.
  // La mesure initiale et les mesures « à chaud » (mutation en cours, scroll) passent
  // `false` pour ne jamais latcher un conteneur vide/placeholder.
  const evaluate = React.useCallback((el: HTMLElement, concludeFits: boolean): void => {
    const { scrollTop, scrollHeight, clientHeight } = el;
    // Zone non encore mesurable (layout absent, hauteur nulle) : on ne conclut RIEN.
    if (scrollHeight <= 0 || clientHeight <= 0) {
      return;
    }
    // Contenu non débordant (« tient entièrement ») : concluant UNIQUEMENT sur une
    // mesure stabilisée (concludeFits) — sinon un conteneur pas encore peuplé « tient »
    // à tort.
    const fitsEntirely = scrollHeight <= clientHeight + SCROLL_TOLERANCE_PX;
    // Défilement arrivé au bas (à la tolérance près) : preuve de lecture RÉELLE, valable
    // à tout instant (le contenu déborde donc est présent).
    const reachedBottom = scrollTop + clientHeight >= scrollHeight - SCROLL_TOLERANCE_PX;
    const fullyRead = reachedBottom || (fitsEntirely && concludeFits);
    // Ratio parcouru borné à [0, 1]. Parcouru → 1 ; débordant → fraction visible ; « tient »
    // mais pas encore conclu → 0 (on ne gonfle pas la barre de progression prématurément).
    const ratio = fullyRead
      ? 1
      : fitsEntirely
        ? 0
        : Math.min(1, Math.max(0, (scrollTop + clientHeight) / scrollHeight));
    setProgress((prev) => (ratio > prev ? ratio : prev));
    // NON monotone (lève le latch prématuré) : reflète l'état COURANT. Un contenu qui
    // déborde de nouveau sans que le bas soit atteint fait repasser `reachedEnd` à false.
    setReachedEnd(fullyRead);
  }, []);

  const ref = React.useCallback(
    (el: HTMLElement | null): void => {
      cleanupRef.current?.();
      cleanupRef.current = null;
      if (el === null) {
        return;
      }
      // Mesure initiale : ne conclut PAS « tient entièrement » — le contenu peut ne pas
      // être encore injecté (RichHtmlView useEffect, pages pdf.js). N'établit que
      // reachedBottom si (rare) déjà défilé au montage.
      evaluate(el, false);

      // Défilement RÉEL : le contenu est présent et débordant → conclusion normale.
      const onScroll = (): void => {
        evaluate(el, true);
      };
      el.addEventListener('scroll', onScroll, { passive: true });

      // Population TARDIVE du contenu (injection HTML, pages pdf.js) : chaque mutation
      // du sous-arbre change `scrollHeight`. On ré-évalue immédiatement (peut LEVER un
      // latch prématuré si ça déborde), puis on n'autorise la conclusion « tient
      // entièrement » qu'après STABILISATION (debounce) — ce qui absorbe le
      // `replaceChildren()` transitoire de pdf.js (conteneur momentanément vide) et les
      // ajouts de pages successifs. Un MutationObserver ne se déclenche que sur un
      // changement RÉEL du DOM (postérieur à l'attache) : un placeholder inchangé
      // (attente réseau) ne conclut donc jamais « tient » à tort.
      let settleTimer: ReturnType<typeof setTimeout> | undefined;
      const onContentMutated = (): void => {
        evaluate(el, false);
        if (settleTimer !== undefined) {
          clearTimeout(settleTimer);
        }
        settleTimer = setTimeout(() => {
          evaluate(el, true);
        }, CONTENT_SETTLE_MS);
      };

      let observer: MutationObserver | undefined;
      if (typeof MutationObserver !== 'undefined') {
        observer = new MutationObserver(onContentMutated);
        observer.observe(el, {
          childList: true,
          subtree: true,
          characterData: true,
          attributes: true,
        });
      }

      cleanupRef.current = () => {
        el.removeEventListener('scroll', onScroll);
        if (settleTimer !== undefined) {
          clearTimeout(settleTimer);
        }
        observer?.disconnect();
      };
    },
    [evaluate],
  );

  return { ref, reachedEnd, progress };
}
